import {uniqBy} from "lodash"
import {FC} from "react"

import {ReactionsWithForm} from "@frontend/components"
import thinkingAnimation from "@frontend/design/animations/thinking.svg"
import {useTranslation} from "@frontend/i18n"
import {
  type Message as MessageModel,
  confirmationThreshold,
  getConfirmedCausesFromMessage,
  getContentFromMessage,
  getLikelyCausesFromMessage,
  isModelNumberRequestMessage,
} from "@ri2/db/client"
import {css} from "@styled-system/css"
import {vstack} from "@styled-system/patterns"

import {CompatiblePartCard} from "./CompatiblePartCard"
import {InlineConfirmedCauseCard} from "./InlineConfirmedCauseCard"
import {InlineDiagnosisCauseCard} from "./InlineDiagnosisCauseCard"
import {PartsCarousel} from "./PartsCarousel"
import {EventLayout} from "./event-layout"
import {MessageContent} from "./message-content"
import {sortConfirmedPartsByMessageShortcodes} from "./partShortcodes"

interface Props {
  caseId: string
  className?: string
  message: MessageModel
  previouslyConfirmedCauseIds: string[]
  previouslyLikelyCauseIds: string[]
  isReceivingMessage: boolean
  receivingMessageStatus: string | null
  isMostRecentMessage: boolean
  onSelectMedia: (url: string) => void
  /** When true (widget mode), use compact chat layout. */
  compact?: boolean
  /** When true (agent-repairclinic), show inline part cards in chat. */
  ecommerceContentSupport?: boolean
  /** When true, show confirmed cause cards inline in chat. */
  inlineConfirmedCauseCards?: boolean
  /** When true, show likely cause cards inline in chat. */
  inlineLikelyCauseCards?: boolean
  /** Model number of the product, used for add-to-cart in part cards. */
  modelNumber?: string | null
}

export const Message: FC<Props> = (props) => {
  const mode = getModeFromProps(props)
  const t = useTranslation()

  const text = getTextFromMode(mode, t)

  const isThinkingOrAI = props.message.type === "ai"

  const questionMedia =
    props.message.type === "ai"
      ? uniqBy(
          [
            ...(props.message.content.questionImages ?? []),
            ...(props.message.content.questionVideos ?? []),
            ...(props.message.content.partTestingVideos ?? []),
          ],
          (element) => element.url,
        )
      : (props.message.content.images ?? [])
  const helperMedia =
    props.message.type === "ai"
      ? uniqBy(
          [
            ...(props.message.content.helperQuestionImages ?? []),
            ...(props.message.content.helperQuestionVideos ?? []),
          ],
          (element) => element.url,
        )
      : []
  const disassemblyVideos =
    props.message.type === "ai"
      ? (uniqBy(
          props.message.content.disassemblyVideos,
          (element) => element.url,
        ) ?? [])
      : []
  const partImages =
    props.message.type === "ai" ? props.message.content.partImages : undefined
  const questionDocuments =
    props.message.type === "ai"
      ? (props.message.content.questionDocuments ?? [])
      : []

  const timestamp = props.message.createdAt

  const confirmedCausesOnMessage = getConfirmedCausesFromMessage(props.message)

  const inlineConfirmedCausesToShow =
    props.inlineConfirmedCauseCards && props.message.type === "ai"
      ? confirmedCausesOnMessage
      : []

  const newlyLikelyCauses =
    props.inlineLikelyCauseCards && props.ecommerceContentSupport
      ? getLikelyCausesFromMessage(props.message)
          .filter(({probability}) => probability < confirmationThreshold)
          .filter(
            ({causeId}) =>
              !props.previouslyLikelyCauseIds.includes(causeId) &&
              !props.previouslyConfirmedCauseIds.includes(causeId),
          )
          .filter((cause) => cause.part)
      : []

  const isAskModelNumberMessage =
    props.message.type === "ai" && isModelNumberRequestMessage(props.message)
  const shouldShowReactions =
    props.message.type === "ai" &&
    !props.isReceivingMessage &&
    !isSkillLevelQuestionMessage(props.message)

  const displayConfirmedParts =
    props.message.type === "ai" &&
    props.message.content.confirmedParts &&
    props.message.content.confirmedParts.length > 0
      ? sortConfirmedPartsByMessageShortcodes(
          props.message.content.confirmedParts,
          props.message.content.question,
        )
      : null

  return (
    <EventLayout
      isThinkingOrAI={isThinkingOrAI}
      timestamp={timestamp}
      className={props.className}
    >
      <div
        className={css({
          width: "100%",
          maxWidth: 570,
          display: "flex",
          minWidth: 0,
          flexDirection: "column",
          gap: props.compact ? 2 : 12,
          color: "text.primary",
          textStyle: "body",
          fontSize: {base: isThinkingOrAI ? 18 : 16, desktop: 16},
        })}
      >
        {(text || questionMedia.length > 0 || questionDocuments.length > 0) && (
          <div
            className={vstack({
              alignItems: "stretch",
            })}
          >
            <MessageContent
              isThinkingOrAi={isThinkingOrAI}
              message={text ?? ""}
              caseId={props.caseId}
              questionMedia={questionMedia}
              helperMedia={helperMedia}
              questionDocuments={questionDocuments}
              disassemblyVideos={disassemblyVideos}
              partImages={partImages}
              linkableParts={
                props.message.type === "ai" && props.ecommerceContentSupport
                  ? uniqBy(
                      [
                        ...(props.message.content.confirmedParts ?? []),
                        ...(props.message.content.confirmedPart
                          ? [props.message.content.confirmedPart]
                          : []),
                        ...(props.message.content.suggestedParts?.map(
                          (s) => s.part,
                        ) ?? []),
                      ],
                      (p) => p.id,
                    )
                  : undefined
              }
              onSelectMedia={props.onSelectMedia}
              isAskModelNumberMessage={isAskModelNumberMessage}
              isWidget={!!props.compact}
            />
          </div>
        )}
        {!!props.receivingMessageStatus &&
          !text &&
          questionMedia.length === 0 && (
            <div
              data-test-id="pending-status"
              className={css({display: "flex"})}
            >
              <p className={css({marginRight: 2, fontWeight: "500"})}>
                {props.receivingMessageStatus}
              </p>
              <img src={thinkingAnimation} height={15} width={15} />
            </div>
          )}

        {/* Show compatible part card(s) when parts were validated during fact-finding */}
        {props.ecommerceContentSupport &&
          props.message.type === "ai" &&
          !props.isReceivingMessage &&
          (displayConfirmedParts && displayConfirmedParts.length > 0 ? (
            <div className={css({marginTop: props.compact ? "8px" : "16px"})}>
              {displayConfirmedParts.length === 1 ? (
                <CompatiblePartCard
                  part={displayConfirmedParts[0]}
                  caseId={props.caseId}
                  modelNumber={props.modelNumber ?? null}
                  compact={props.compact}
                  animatedGradientBorder={true}
                />
              ) : (
                <PartsCarousel
                  parts={displayConfirmedParts}
                  caseId={props.caseId}
                  modelNumber={props.modelNumber ?? null}
                  compact={props.compact}
                />
              )}
            </div>
          ) : (
            props.message.content.confirmedPart && (
              <div className={css({marginTop: props.compact ? "8px" : "16px"})}>
                <CompatiblePartCard
                  part={props.message.content.confirmedPart}
                  caseId={props.caseId}
                  modelNumber={props.modelNumber ?? null}
                  compact={props.compact}
                  animatedGradientBorder={true}
                />
              </div>
            )
          ))}

        {props.message.type === "ai" &&
          !props.isReceivingMessage &&
          inlineConfirmedCausesToShow.length > 0 && (
            <div
              className={vstack({
                gap: 20,
                flexWrap: "wrap",
                alignItems: "flex-start",
                marginTop: 20,
              })}
            >
              {inlineConfirmedCausesToShow.map((cause) => (
                <div
                  key={cause.id}
                  className={vstack({
                    gap: 10,
                    alignItems: "flex-start",
                    width: "100%",
                  })}
                >
                  {props.ecommerceContentSupport ? (
                    <InlineDiagnosisCauseCard
                      cause={cause}
                      variant="confirmed"
                      caseId={props.caseId}
                      modelNumber={props.modelNumber ?? null}
                      compact={props.compact}
                    />
                  ) : (
                    <InlineConfirmedCauseCard
                      cause={cause}
                      caseId={props.caseId}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

        {props.message.type === "ai" && shouldShowReactions && (
          <>
            {newlyLikelyCauses.length > 0 && (
              <div
                className={vstack({
                  gap: 20,
                  flexWrap: "wrap",
                  alignItems: "flex-start",
                  marginTop: 20,
                })}
              >
                {newlyLikelyCauses.map((cause) => (
                  <div
                    key={cause.id}
                    className={vstack({
                      gap: 10,
                      alignItems: "flex-start",
                      width: "100%",
                    })}
                  >
                    <InlineDiagnosisCauseCard
                      cause={cause}
                      variant="likely"
                      caseId={props.caseId}
                      modelNumber={props.modelNumber ?? null}
                      compact={props.compact}
                    />
                  </div>
                ))}
              </div>
            )}
            <ReactionsWithForm
              on={{
                on: "message",
                messageId: props.message.id,
              }}
              tooltipVariant="dark"
              isInline
              tooltipPlacement={props.isMostRecentMessage ? "top" : "bottom"}
              currentFeedback={props.message.feedback}
              transparent
              // z index needed to make sure the tooltip is above the cause card if one
              className={css({marginTop: props.compact ? 1 : 8, zIndex: 1})}
            />
          </>
        )}
      </div>
    </EventLayout>
  )
}

type Mode =
  | {
      mode: "message"
      message: MessageModel
      onChooseMultipleChoiceOption?: (option: string) => void
    }
  | {
      mode: "thinking"
    }

const getModeFromProps = (props: Props): Mode =>
  "message" in props
    ? {
        mode: "message",
        message: props.message,
      }
    : {mode: "thinking"}

const getTextFromMode = (
  mode: Mode,
  t: ReturnType<typeof useTranslation>,
): string | undefined => {
  switch (mode.mode) {
    case "message":
      return getContentFromMessage(mode.message)
    case "thinking":
      return t("conversation.thinking")
  }
}

const isSkillLevelQuestionMessage = (message: MessageModel): boolean =>
  message.type === "ai" &&
  (
    message.content.multipleChoiceAnswers?.map((answer) =>
      typeof answer === "string" ? answer : answer.text,
    ) ?? []
  ).join("|") === "Beginner|Intermediate|Expert"
